import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import type {
  EntityFiltersState,
  FilterableEntity,
  JobFilters,
  OptimizationFilters,
  PaginationState,
  RunFilters,
} from "@src/types/filters";
import { getDefaultEntityFilters } from "@src/types/filters";

/**
 * Redux slice for managing filter and pagination state across all filterable entities.
 *
 * Architecture:
 * - URL is source of truth (prepared for future URL persistence)
 * - Redux provides cross-component access and sync
 * - Each entity has independent filter + pagination state
 * - Page navigation resets entity state to defaults
 */
const initialState: EntityFiltersState = getDefaultEntityFilters();

const entityFiltersSlice = createSlice({
  name: "entityFilters",
  initialState,
  reducers: {
    // Apply filters for an entity (called when user clicks "Filter" button)
    setFilters(
      state: EntityFiltersState,
      action: PayloadAction<{
        entity: FilterableEntity;
        filters: RunFilters | JobFilters | OptimizationFilters;
      }>,
    ) {
      const { entity, filters } = action.payload;

      switch (entity) {
        case "jobs":
          state.jobs.filters = filters as JobFilters;
          break;
        case "backtests":
          state.backtests.filters = filters as RunFilters;
          break;
        case "executions":
          state.executions.filters = filters as RunFilters;
          break;
        case "optimizations":
          state.optimizations.filters = filters as OptimizationFilters;
          break;
      }
      // Reset to first page when filters change
      state[entity].pagination.page = 0;
    },

    // Update pagination for an entity (immediate actions)
    setPagination(
      state: EntityFiltersState,
      action: PayloadAction<{ entity: FilterableEntity; pagination: Partial<PaginationState> }>,
    ) {
      const { entity, pagination } = action.payload;
      state[entity].pagination = { ...state[entity].pagination, ...pagination };
    },

    // Reset specific entity to defaults (called on page leave)
    resetEntity(state: EntityFiltersState, action: PayloadAction<FilterableEntity>) {
      const entity = action.payload;
      const defaults = getDefaultEntityFilters();
      switch (entity) {
        case "jobs":
          state.jobs = defaults.jobs;
          break;
        case "backtests":
          state.backtests = defaults.backtests;
          break;
        case "executions":
          state.executions = defaults.executions;
          break;
        case "optimizations":
          state.optimizations = defaults.optimizations;
          break;
      }
    },

    // Reset all entities (for logout or app reset)
    resetAll(state: EntityFiltersState) {
      Object.assign(state, getDefaultEntityFilters());
    },
  },
});

export const entityFiltersActions = entityFiltersSlice.actions;
export const entityFiltersReducer = entityFiltersSlice.reducer;
